import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Field, Select, TextInput } from '../components/ui';
import { Captcha } from '../components/Captcha';
import { SiteFooter } from '../components/Layout';
import { citiesForState, statesForSelect, stateFromGstin, type FoodType } from '@kortex/shared';
import { ApiError, api } from '../lib/api';
import { useAuth, type CaptchaFields } from '../lib/auth';
import { money } from '../lib/format';

interface PublicPlan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  priceMonthly: number;
  trialDays: number;
  maxProperties: number;
  maxUsers: number;
  features: string[];
}

const BUSINESS_FIELDS = [
  { name: 'name', label: 'Hotel name', placeholder: 'Sunrise Residency', required: true },
  {
    name: 'legalName',
    label: 'Registered legal name',
    placeholder: 'Sunrise Hospitality Pvt Ltd',
    required: false,
  },
  {
    name: 'email',
    label: 'Hotel email',
    type: 'email',
    placeholder: 'contact@sunrise.in',
    required: true,
  },
  { name: 'phone', label: 'Hotel phone', placeholder: '+91 80 4000 1234', required: true },
  { name: 'gstin', label: 'GSTIN', placeholder: '29AABCK1234M1Z5', required: true },
  {
    name: 'fssai',
    label: 'FSSAI licence',
    placeholder: '12345678901234',
    required: true,
  },
  {
    name: 'address',
    label: 'Address',
    placeholder: '14 MG Road, Bengaluru 560001',
    required: true,
    wide: true,
  },
] as const;

const OTHER_CITY = '__other__';

interface OtpChallenge {
  /** False when the platform has no outgoing mail, so nothing can be sent. */
  required: boolean;
  sent: boolean;
  reason?: string;
  expiresInMinutes: number;
}

type FieldErrors = Record<string, string>;

/**
 * Public self-registration.
 *
 * Every business field the tax invoice needs is collected up front and marked
 * required, so a hotel cannot end up inside the product unable to raise a
 * compliant bill.
 */
export function Register() {
  const { status, register } = useAuth();
  const [business, setBusiness] = useState<Record<string, string>>({});
  const [owner, setOwner] = useState<Record<string, string>>({});
  const [planCode, setPlanCode] = useState('FREE');
  const [captcha, setCaptcha] = useState<CaptchaFields>({});
  const [captchaToken, setCaptchaToken] = useState(0);
  const [cityMode, setCityMode] = useState<'list' | 'other'>('list');
  const [serves, setServes] = useState({ veg: false, nonVeg: false });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [otp, setOtp] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const plans = useQuery({
    queryKey: ['public-plans'],
    queryFn: () => api.get<PublicPlan[]>('/api/public/plans'),
  });

  if (status === 'authenticated') return <Navigate to="/" replace />;

  const set = (target: 'business' | 'owner', field: string, value: string) => {
    const update = target === 'business' ? setBusiness : setOwner;
    update((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${target}.${field}`];
      return next;
    });
  };

  /**
   * Asks for a code before the form is submitted.
   *
   * The answer says whether verification is even switched on: on a platform
   * with no mail server the API cannot send anything, and says so, rather than
   * leaving somebody waiting for a mail that will never arrive.
   */
  const requestOtp = async () => {
    const email = (business.email ?? '').trim();
    if (!email) {
      setErrors((prev) => ({ ...prev, 'business.email': 'Enter the hotel email first' }));
      return;
    }
    setOtpBusy(true);
    setFormError(null);
    try {
      setChallenge(
        await api.post<OtpChallenge>('/api/public/register/otp', {
          email,
          hotelName: (business.name ?? '').trim(),
        }),
      );
    } catch (caught) {
      setFormError(
        caught instanceof ApiError ? caught.message : 'Could not send the code. Try again.',
      );
    } finally {
      setOtpBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setFormError(null);
    setErrors({});

    try {
      await register({
        business: { ...business, ...(foodType ? { foodType } : {}) },
        owner,
        planCode,
        ...captcha,
        acceptTerms,
        otp: otp.trim() || undefined,
      });
    } catch (caught) {
      if (caught instanceof ApiError && Array.isArray(caught.details)) {
        const mapped: FieldErrors = {};
        for (const detail of caught.details as Array<{ path: string; message: string }>) {
          mapped[detail.path] = detail.message;
        }
        setErrors(mapped);
        setFormError('Check the highlighted fields.');
      } else {
        setFormError(
          caught instanceof ApiError
            ? caught.message
            : 'Could not complete registration. Try again.',
        );
      }
      // The challenge is spent by the attempt either way.
      setCaptchaToken((token) => token + 1);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Two ticks, three answers.
   *
   * Ticking both boxes is what "both" means, so the form does not need a third
   * option that contradicts the other two - and it leaves nothing selected as a
   * genuinely unanswered question rather than a silent default.
   */
  const foodType: FoodType | null =
    serves.veg && serves.nonVeg ? 'BOTH' : serves.veg ? 'VEG' : serves.nonVeg ? 'NONVEG' : null;

  const selected = plans.data?.find((p) => p.code === planCode);
  const states = statesForSelect();
  const cities = citiesForState(business.stateCode);

  return (
    <div className="auth-shell">
      <aside className="auth-aside">
        <div className="row gap-6">
          <div className="brand-mark">K</div>
          <div className="brand-name" style={{ fontSize: 17 }}>
            Kortex HMS
          </div>
        </div>

        <div>
          <h1>Run your hotel on one system.</h1>
          <p>
            Billing, kitchen, stock, procurement, payroll and the front desk — set up in a couple of
            minutes, free for the first{' '}
            {plans.data?.find((p) => p.code === 'FREE')?.trialDays ?? 30} days. No card required.
          </p>

          <div className="auth-points">
            <div>
              <span>✓</span>
              <span>
                <b>GST-ready from the first bill.</b> Your GSTIN and place of supply drive CGST/SGST
                or IGST automatically.
              </span>
            </div>
            <div>
              <span>✓</span>
              <span>
                <b>Nothing to install.</b> Works on the front desk PC, a tablet in the kitchen and
                your phone.
              </span>
            </div>
            <div>
              <span>✓</span>
              <span>
                <b>Your data stays yours.</b> Every hotel is isolated; you can export at any time.
              </span>
            </div>
          </div>
        </div>

        <div className="small" style={{ color: '#7fa9a4' }}>
          Already registered?{' '}
          <Link to="/sign-in" style={{ color: '#cfe3e0' }}>
            Sign in
          </Link>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-card" style={{ maxWidth: 720 }}>
          <h2>Register your hotel</h2>
          <div className="sub">
            These details appear on every tax invoice you raise, so they are all required.
          </div>

          <form onSubmit={submit} className="col gap-16">
            {formError ? <div className="form-error">{formError}</div> : null}

            <section className="card">
              <div className="card-head">
                <h3>Business profile</h3>
                <span className="chip">Required</span>
              </div>
              <div className="card-body">
                <div className="form-grid">
                  {BUSINESS_FIELDS.map((field) => (
                    <div
                      key={field.name}
                      className={'wide' in field && field.wide ? 'span2' : undefined}
                    >
                      <TextInput
                        label={field.required ? `${field.label} *` : field.label}
                        type={'type' in field ? field.type : 'text'}
                        placeholder={field.placeholder}
                        required={field.required}
                        value={business[field.name] ?? ''}
                        error={errors[`business.${field.name}`]}
                        onChange={(e) => {
                          set('business', field.name, e.target.value);
                          // The first two digits of a GSTIN are the state, so
                          // fill it in rather than asking twice.
                          if (field.name === 'gstin' && !business.stateCode) {
                            const derived = stateFromGstin(e.target.value.toUpperCase());
                            if (derived) set('business', 'stateCode', derived.code);
                          }
                        }}
                      />
                    </div>
                  ))}

                  <Select
                    label="State *"
                    required
                    value={business.stateCode ?? ''}
                    error={errors['business.stateCode']}
                    onChange={(e) => {
                      set('business', 'stateCode', e.target.value);
                      // The old city belongs to the old state.
                      set('business', 'city', '');
                      setCityMode('list');
                    }}
                  >
                    <option value="">Choose a state…</option>
                    {states.map((state) => (
                      <option key={state.code} value={state.code}>
                        {state.name} ({state.code})
                      </option>
                    ))}
                  </Select>

                  {cityMode === 'list' && cities.length > 0 ? (
                    <Select
                      label="City *"
                      required
                      value={business.city ?? ''}
                      error={errors['business.city']}
                      onChange={(e) => {
                        if (e.target.value === OTHER_CITY) {
                          setCityMode('other');
                          set('business', 'city', '');
                        } else {
                          set('business', 'city', e.target.value);
                        }
                      }}
                    >
                      <option value="">Choose a city…</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                      <option value={OTHER_CITY}>Somewhere else…</option>
                    </Select>
                  ) : (
                    <TextInput
                      label="City *"
                      required
                      placeholder={
                        business.stateCode ? 'Type your town or city' : 'Choose a state first'
                      }
                      disabled={!business.stateCode}
                      hint={
                        cities.length > 0 ? (
                          <button
                            type="button"
                            className="btn ghost sm"
                            onClick={() => {
                              setCityMode('list');
                              set('business', 'city', '');
                            }}
                          >
                            Pick from the list instead
                          </button>
                        ) : undefined
                      }
                      value={business.city ?? ''}
                      error={errors['business.city']}
                      onChange={(e) => set('business', 'city', e.target.value)}
                    />
                  )}

                  <TextInput
                    label="PIN code *"
                    required
                    inputMode="numeric"
                    placeholder="560001"
                    hint="It goes on every invoice you issue"
                    value={business.pincode ?? ''}
                    error={errors['business.pincode']}
                    onChange={(e) =>
                      set('business', 'pincode', e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                  />

                  <Field
                    label="What do you serve? *"
                    className="span2"
                    error={errors['business.foodType']}
                    hint={
                      foodType === 'BOTH'
                        ? 'Your starter menu will cover both, split across the kitchen stations.'
                        : foodType === 'VEG'
                          ? 'You will start with a vegetarian menu.'
                          : foodType === 'NONVEG'
                            ? 'You will start with the non-vegetarian dishes plus the staples every kitchen serves.'
                            : 'Tick both if your kitchen does both.'
                    }
                  >
                    <div className="row wrap gap-16" style={{ paddingTop: 4 }}>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={serves.veg}
                          onChange={(e) => {
                            setServes((prev) => ({ ...prev, veg: e.target.checked }));
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next['business.foodType'];
                              return next;
                            });
                          }}
                        />
                        <span>Vegetarian</span>
                      </label>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={serves.nonVeg}
                          onChange={(e) => {
                            setServes((prev) => ({ ...prev, nonVeg: e.target.checked }));
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next['business.foodType'];
                              return next;
                            });
                          }}
                        />
                        <span>Non-vegetarian</span>
                      </label>
                    </div>
                  </Field>
                </div>
                <p className="small muted" style={{ marginTop: 10 }}>
                  The state must match the first two digits of your GSTIN — it decides whether a
                  bill carries CGST/SGST or IGST. The FSSAI licence is 14 digits and is printed on
                  every food bill you raise. What you serve decides the starter menu we set up for
                  you - every item arrives unpriced and switched off, ready for you to price.
                </p>
              </div>
            </section>

            <section className="card">
              <div className="card-head">
                <h3>Your owner account</h3>
              </div>
              <div className="card-body">
                <div className="form-grid">
                  <TextInput
                    label="Your name *"
                    required
                    value={owner.name ?? ''}
                    error={errors['owner.name']}
                    onChange={(e) => set('owner', 'name', e.target.value)}
                  />
                  <TextInput
                    label="Your phone"
                    value={owner.phone ?? ''}
                    error={errors['owner.phone']}
                    onChange={(e) => set('owner', 'phone', e.target.value)}
                  />
                  <TextInput
                    label="Sign-in email *"
                    type="email"
                    autoComplete="username"
                    required
                    value={owner.email ?? ''}
                    error={errors['owner.email']}
                    onChange={(e) => set('owner', 'email', e.target.value)}
                  />
                  <TextInput
                    label="Password *"
                    type="password"
                    autoComplete="new-password"
                    required
                    hint="At least 12 characters, with upper case, lower case and a number."
                    value={owner.password ?? ''}
                    error={errors['owner.password']}
                    onChange={(e) => set('owner', 'password', e.target.value)}
                  />
                </div>
              </div>
            </section>

            <section className="card">
              <div className="card-head">
                <h3>Choose a plan</h3>
                <span className="small muted">Change or upgrade at any time</span>
              </div>
              <div className="card-body">
                {plans.isLoading ? (
                  <div className="small muted">Loading plans…</div>
                ) : (
                  <div className="plan-grid">
                    {(plans.data ?? []).map((plan) => (
                      <button
                        type="button"
                        key={plan.code}
                        className={`plan-card${planCode === plan.code ? ' selected' : ''}`}
                        onClick={() => setPlanCode(plan.code)}
                        aria-pressed={planCode === plan.code}
                      >
                        <div className="row between">
                          <b>{plan.name}</b>
                          {plan.trialDays > 0 ? (
                            <span className="chip ok">{plan.trialDays}-day trial</span>
                          ) : null}
                        </div>
                        <div className="plan-price">
                          {plan.priceMonthly === 0 ? 'Free' : money(plan.priceMonthly)}
                          {plan.priceMonthly > 0 ? (
                            <span className="muted small"> /month</span>
                          ) : null}
                        </div>
                        {plan.description ? (
                          <p className="small muted">{plan.description}</p>
                        ) : null}
                        <ul className="plan-features">
                          {(plan.features ?? []).map((feature) => (
                            <li key={feature}>{feature}</li>
                          ))}
                        </ul>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section className="card">
              <div className="card-head">
                <h2>Verify your email</h2>
                <span className="small muted">So we know the address reaches you</span>
              </div>
              <div className="card-body col gap-16">
                <div className="row gap-6 wrap" style={{ alignItems: 'flex-end' }}>
                  <TextInput
                    label="Code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    hint={
                      challenge?.sent
                        ? `Sent to ${business.email} - valid ${challenge.expiresInMinutes} minutes`
                        : 'We email a six-digit code to your hotel email'
                    }
                    value={otp}
                    error={errors['otp']}
                    disabled={busy || challenge?.required === false}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  />
                  <button
                    type="button"
                    className="btn"
                    disabled={busy || otpBusy}
                    onClick={() => void requestOtp()}
                  >
                    {otpBusy ? 'Sending...' : challenge ? 'Send again' : 'Email me a code'}
                  </button>
                </div>
                {challenge && challenge.required === false ? (
                  <p className="small muted">
                    Email verification is switched off on this server, so you can register without a
                    code.
                  </p>
                ) : challenge && !challenge.sent ? (
                  <p className="small warn">
                    We could not send it{challenge.reason ? `: ${challenge.reason}` : ''}. Mail is
                    switched on for this server, so registration needs the code — ask whoever runs
                    this Kortex to check Platform → Outgoing Mail, then try again.
                  </p>
                ) : null}
              </div>
            </section>

            <section className="card">
              <div className="card-body">
                <Captcha
                  value={captcha}
                  onChange={setCaptcha}
                  error={errors['captcha']}
                  disabled={busy}
                  reloadToken={captchaToken}
                />
              </div>
            </section>

            <label className="accept-terms">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => {
                  setAcceptTerms(e.target.checked);
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next['acceptTerms'];
                    return next;
                  });
                }}
              />
              <span>
                I accept the{' '}
                {/*
                  Opens in a new tab, so a half-filled form is not lost to
                  reading it. The click is stopped from reaching the label,
                  which would otherwise tick the box on the way past - agreeing
                  on somebody's behalf because they wanted to read first.
                */}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  terms and conditions
                </a>
                .
              </span>
            </label>
            {errors['acceptTerms'] ? (
              <div className="field-error" style={{ marginTop: -6 }}>
                {errors['acceptTerms']}
              </div>
            ) : null}

            <button
              className="btn primary lg block"
              type="submit"
              disabled={busy || !acceptTerms}
            >
              {busy
                ? 'Creating your hotel…'
                : selected && selected.trialDays > 0
                  ? `Start my ${selected.trialDays}-day free trial`
                  : 'Create my hotel'}
            </button>

            <p className="small muted center">
              You also agree to keep your GSTIN and business details accurate — they
              appear on the invoices you issue to guests.
            </p>
          </form>
        </div>

        <SiteFooter />
      </main>
    </div>
  );
}
